import { Flex } from '@kognitos/lattice';
import type { TResolvedValue } from '@/modules/outputs/types';
import { ValueChip } from '@/modules/outputs/components/ValueChip';
import { formatDisplayName } from '@/shared/utils';

interface IRunLogInputsContentProps {
  /** Pre-filtered chip-bound entries from the parent's resolved input map */
  entries: [string, TResolvedValue][];
}

/**
 * Renders a step's inputs as compact tag chips with a hover popover.
 *
 * Receives already-filtered entries from the parent (RunLogTimeline) so that
 * partition decisions live in one place. Returns null when given an empty
 * list as a defensive guard.
 *
 * Chips lead with the input's name (`showName`): this is the only place a
 * step's argument names appear, and without them several list arguments render
 * as a row of indistinguishable "List" chips.
 *
 * @param {IRunLogInputsContentProps} props - Component props
 * @returns {JSX.Element | null} Chip row or null if no entries
 */
export const RunLogInputsContent = ({ entries }: IRunLogInputsContentProps) => {
  if (entries.length === 0) {
    return null;
  }

  return (
    <Flex gap={1} wrap="wrap">
      {entries.map(([key, value]) => (
        <ValueChip
          key={key}
          name={formatDisplayName(key)}
          value={value}
          showName
        />
      ))}
    </Flex>
  );
};
